import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useDismissOnEscape, useFocusTrap } from "../../lib/a11y";

/** Readable workflow modal backdrop — mobile bottom sheet, centered on sm+.
 *  `!m-0` guards against parent `space-y-*` margins if portal ever fails. */
/** Above sticky chrome and the Dev bar (z-50) so modal actions stay clickable. */
export const WORKFLOW_MODAL_BACKDROP =
  "fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink/70 p-0 sm:p-6 !m-0";

/**
 * Panel surfaces use theme tokens (--surface / --ink / --border-subtle), not
 * hardcoded white. Public host themes sync onto <html>, so light ink on white
 * would fail contrast on dark themes (midnight / neon).
 */
export const WORKFLOW_MODAL_PANEL_SCROLL =
  "bg-surface text-ink border border-subtle shadow-card rounded-xl2 w-full max-h-[92vh] overflow-y-auto pop-in";

/** Flex column panel with sticky footer (Safety check / person sheet). */
export const WORKFLOW_MODAL_PANEL_SHEET =
  "bg-surface text-ink border border-subtle border-b-0 sm:border-b shadow-card rounded-t-xl2 sm:rounded-xl2 w-full max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom)))] sm:max-h-[92vh] flex flex-col overflow-hidden pop-in";

export function ModalCloseButton({ onClick, testId, iconSize = "w-4 h-4", className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md2 border border-subtle p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-ink hover:bg-surface-muted shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 ${className}`}
      aria-label="Close"
      data-testid={testId}
    >
      <X className={iconSize} strokeWidth={3} />
    </button>
  );
}

export function WorkflowModalHeader({
  eyebrow,
  title,
  titleId,
  subtitle,
  leading,
  actions,
  onClose,
  closeTestId,
  sticky = false,
  align = "start",
  className = "",
}) {
  const stickyClass = sticky ? "sticky top-0 z-10 bg-surface" : "";
  const alignClass = align === "center" ? "items-center" : "items-start";

  return (
    <div
      className={`shrink-0 border-b border-subtle p-4 sm:p-5 flex ${alignClass} justify-between gap-3 ${stickyClass} ${className}`}
    >
      <div className="min-w-0 flex-1 flex items-center gap-3">
        {leading ? <div className="shrink-0">{leading}</div> : null}
        <div className="min-w-0">
          {eyebrow && (
            <div className="font-ui text-[12px] font-medium text-ink/70 flex items-center gap-1.5">
              {eyebrow}
            </div>
          )}
          <h2
            id={titleId}
            className={`font-ui font-semibold text-[20px] sm:text-[22px] leading-tight tracking-tight truncate text-ink ${eyebrow ? "mt-0.5" : ""}`}
          >
            {title}
          </h2>
          {subtitle ? (
            <div className="text-[13px] text-ink/70 mt-0.5 min-w-0">{subtitle}</div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {actions}
        {onClose && <ModalCloseButton onClick={onClose} testId={closeTestId} />}
      </div>
    </div>
  );
}

export function WorkflowModalBody({ children, className = "", sheet = false }) {
  if (sheet) {
    return (
      <div className={`flex-1 overflow-y-auto overscroll-contain p-5 sm:p-6 ${className}`}>
        {children}
      </div>
    );
  }
  return <div className={`p-5 sm:p-6 space-y-4 ${className}`}>{children}</div>;
}

export function WorkflowModalFooter({ children, className = "", sticky = false, safeArea = false }) {
  const stickyClass = sticky ? "sticky bottom-0 bg-surface" : "";
  const safeClass = safeArea
    ? "px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:px-5 sm:pb-4"
    : "p-5 sm:p-6";

  return (
    <div className={`shrink-0 border-t border-subtle ${stickyClass} ${safeClass} ${className}`}>
      {children}
    </div>
  );
}

/**
 * Readable workflow modal shell — portaled to document.body so parent
 * `space-y-*` / overflow never inset the scrim. Backdrop, focus trap, escape.
 * Use layout="sheet" for mobile bottom sheets with sticky footer.
 */
export function WorkflowModalShell({
  dialogRef,
  onClose,
  onBackdropClose,
  testId,
  titleId,
  describedById,
  busy,
  layout = "scroll",
  maxWidthClass = "sm:max-w-[560px]",
  dismissOnEscape = true,
  children,
}) {
  useDismissOnEscape(onClose, dismissOnEscape);
  useFocusTrap(dialogRef, true);

  const panelBase = layout === "sheet" ? WORKFLOW_MODAL_PANEL_SHEET : WORKFLOW_MODAL_PANEL_SCROLL;

  const node = (
    <div
      className={WORKFLOW_MODAL_BACKDROP}
      onClick={onBackdropClose ?? onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`${panelBase} ${maxWidthClass}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedById || undefined}
        aria-busy={busy || undefined}
      >
        {children}
      </div>
    </div>
  );

  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
